import { useState, useEffect } from 'react';
import { Save, Clock, AlertTriangle, Pause, Link, Star } from 'lucide-react';
import { Button, Card, Spinner } from '@centraoneplus/ui';
import { pipelineService } from '@/services/pipeline.service';
import { apiClient } from '@/services/api-client';
import type { Pipeline, PipelineSLAConfig } from '@/types/pipeline';
import { ThemedSelect } from '@components/common/ThemedSelect';

interface SLATabProps {
  pipeline: Pipeline;
  onUpdate: (updatedPipeline: Pipeline) => void;
}

const defaultSLAConfig: PipelineSLAConfig = {
  enabled: false,
  defaultTargets: {
    firstResponseMinutes: 60,
    resolutionMinutes: 480,
    warningThresholdPercent: 80,
  },
  priorityOverrides: {},
  businessHoursOnly: true,
  // Off by default, matching the backend (normalizeSLAConfig): the SLA clock
  // keeps running in Pending unless an admin opts this pipeline in.
  pauseOnPending: false,
};

const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

interface SlaPolicyOption {
  id: string;
  name: string;
  isActive: boolean;
  isDefault?: boolean;
  firstResponseTarget?: number;
  resolutionTarget?: number;
}

export default function SLATab({ pipeline, onUpdate }: SLATabProps) {
  // Merge with defaults to ensure all nested objects exist.
  //
  // The persisted (canonical) slaConfig uses SHORT keys — the backend's
  // normalizeSLAConfig rewrites the form's long keys on save:
  //   defaultTargets.firstResponseMinutes  -> defaultTargets.firstResponse
  //   defaultTargets.resolutionMinutes     -> defaultTargets.resolution
  //   defaultTargets.warningThresholdPercent -> warningThresholds.{firstResponse,resolution}
  //   businessHoursOnly                    -> useBusinessHours
  //   pauseOnPending                       -> pauseConditions.waitingOnCustomer
  //   priorityOverrides                    -> priorityTargets (short keys inside)
  // The form works in the long-key shape, so we map canonical -> form here.
  // Each read tries the short key first, then the long key (covers an
  // in-session save that hasn't been re-fetched), then the default. Reading
  // only the long keys was the bug that made saved values revert to 60/480.
  const [config, setConfig] = useState<PipelineSLAConfig>(() => {
    const raw = pipeline.slaConfig as any;
    const dt = raw?.defaultTargets ?? {};
    const warn = raw?.warningThresholds ?? {};

    const rawOverrides = raw?.priorityTargets ?? raw?.priorityOverrides ?? {};
    const priorityOverrides: Record<string, { firstResponseMinutes?: number; resolutionMinutes?: number }> = {};
    for (const [priority, t] of Object.entries(rawOverrides as Record<string, any>)) {
      if (t && typeof t === 'object') {
        priorityOverrides[priority] = {
          firstResponseMinutes: t.firstResponse ?? t.firstResponseMinutes,
          resolutionMinutes: t.resolution ?? t.resolutionMinutes,
        };
      }
    }

    return {
      enabled: raw?.enabled ?? defaultSLAConfig.enabled,
      defaultTargets: {
        firstResponseMinutes: dt.firstResponse ?? dt.firstResponseMinutes ?? defaultSLAConfig.defaultTargets.firstResponseMinutes,
        resolutionMinutes: dt.resolution ?? dt.resolutionMinutes ?? defaultSLAConfig.defaultTargets.resolutionMinutes,
        warningThresholdPercent: warn.firstResponse ?? warn.resolution ?? dt.warningThresholdPercent ?? defaultSLAConfig.defaultTargets.warningThresholdPercent,
      },
      priorityOverrides,
      businessHoursOnly: raw?.useBusinessHours ?? raw?.businessHoursOnly ?? defaultSLAConfig.businessHoursOnly,
      businessHoursId: raw?.businessHoursId ?? null,
      pauseOnPending: raw?.pauseConditions?.waitingOnCustomer ?? raw?.pauseOnPending ?? defaultSLAConfig.pauseOnPending,
    };
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // SLA source: 'custom' (inline config) or 'policy' (link to existing SLA policy)
  const [slaSource, setSlaSource] = useState<'custom' | 'policy'>(
    (pipeline.slaConfig as any)?.slaPolicyId ? 'policy' : 'custom'
  );
  const [selectedPolicyId, setSelectedPolicyId] = useState<string>(
    (pipeline.slaConfig as any)?.slaPolicyId || ''
  );
  const [availablePolicies, setAvailablePolicies] = useState<SlaPolicyOption[]>([]);
  const [policiesLoading, setPoliciesLoading] = useState(false);

  // Business hours schedules for the custom-SLA schedule picker.
  const [schedules, setSchedules] = useState<
    { id: string; name: string; isActive: boolean; isDefault?: boolean }[]
  >([]);

  useEffect(() => {
    apiClient
      .get('/business-hours')
      .then((res) => setSchedules(res.data?.data || res.data || []))
      .catch(() => {
        /* non-fatal: dropdown just shows tenant-default */
      });
  }, []);

  useEffect(() => {
    // Fetch available SLA policies for the dropdown.
    // Include the currently-linked policy even if it has since been
    // deactivated, so the dropdown doesn't silently lose the selection
    // and send a stale/invalid id back on save. Mark it "(inactive)".
    const currentId = (pipeline.slaConfig as any)?.slaPolicyId;
    setPoliciesLoading(true);
    apiClient
      .get('/sla/policies')
      .then((res) => {
        const all: SlaPolicyOption[] = res.data?.data || res.data || [];
        const policies = all.filter((p) => p.isActive || p.id === currentId);
        setAvailablePolicies(policies);
      })
      .catch(() => {
        setError('Failed to load SLA policies. Try refreshing the page.');
      })
      .finally(() => setPoliciesLoading(false));
  }, [pipeline.slaConfig]);

  const selectedPolicy = availablePolicies.find((p) => p.id === selectedPolicyId);

  const handleChange = (path: string, value: unknown) => {
    const keys = path.split('.');
    const newConfig = { ...config };
    let current: any = newConfig;

    for (let i = 0; i < keys.length - 1; i++) {
      current[keys[i]] = { ...current[keys[i]] };
      current = current[keys[i]];
    }

    current[keys[keys.length - 1]] = value;
    setConfig(newConfig);
    setHasChanges(true);
  };

  const handlePriorityOverride = (priority: string, field: string, value: number) => {
    const overrides = { ...config.priorityOverrides };
    if (!overrides[priority]) {
      overrides[priority] = {};
    }
    overrides[priority] = { ...overrides[priority], [field]: value };
    handleChange('priorityOverrides', overrides);
  };

  const formatMinutes = (minutes?: number): string => {
    if (!minutes) return 'Not set';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const handleSave = async () => {
    if (slaSource === 'policy' && !selectedPolicyId) {
      setError('Select an SLA policy before saving, or switch to custom targets.');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      const saveConfig = {
        ...config,
        slaPolicyId: slaSource === 'policy' ? selectedPolicyId : null,
        useCustom: slaSource === 'custom',
      };
      await pipelineService.updateSLAConfig(pipeline.id, saveConfig);
      onUpdate({ ...pipeline, slaConfig: saveConfig });
      setHasChanges(false);
    } catch (err) {
      console.error('Error saving SLA config:', err);
      setError('Failed to save SLA configuration');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Master Toggle */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Clock className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">SLA Tracking</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Enable service level agreement tracking for this pipeline
              </p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.enabled}
              onChange={(e) => handleChange('enabled', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 dark:bg-gray-700"></div>
          </label>
        </div>
      </Card>

      {config.enabled && (
        <>
          {/* SLA Source Selector */}
          <Card className="p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">SLA Source</h3>
            <div className="space-y-3">
              <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                <input
                  type="radio"
                  name="slaSource"
                  value="policy"
                  checked={slaSource === 'policy'}
                  onChange={() => { setSlaSource('policy'); setHasChanges(true); }}
                  className="mt-1"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                    <Link className="w-4 h-4" />
                    Use existing SLA Policy
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Select a reusable SLA policy configured in Settings &gt; SLA. Shared across pipelines.
                  </p>
                  {slaSource === 'policy' && (
                    <>
                      <ThemedSelect
                        value={selectedPolicyId}
                        onChange={(e) => { setSelectedPolicyId(e.target.value); setHasChanges(true); }}
                        disabled={policiesLoading}
                        className="mt-2 block w-full max-w-xs px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 disabled:opacity-60"
                      >
                        <option value="">
                          {policiesLoading ? 'Loading policies…' : '-- Select SLA Policy --'}
                        </option>
                        {availablePolicies.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                            {p.isDefault ? ' — Default' : ''}
                            {!p.isActive ? ' (inactive)' : ''}
                          </option>
                        ))}
                      </ThemedSelect>

                      {!policiesLoading && availablePolicies.length === 0 && !error && (
                        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                          No SLA policies found. Create one under Settings &gt; SLA first.
                        </p>
                      )}

                      {selectedPolicy && !selectedPolicy.isActive && (
                        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          This policy is currently inactive. Pick another or reactivate it in Settings &gt; SLA.
                        </p>
                      )}

                      {selectedPolicy && (
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                          First response: {formatMinutes(selectedPolicy.firstResponseTarget)} · Resolution:{' '}
                          {formatMinutes(selectedPolicy.resolutionTarget)}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                <input
                  type="radio"
                  name="slaSource"
                  value="custom"
                  checked={slaSource === 'custom'}
                  onChange={() => { setSlaSource('custom'); setHasChanges(true); }}
                  className="mt-1"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Use custom SLA targets</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Define SLA targets specific to this pipeline. Independent from SLA policies.
                  </p>
                </div>
              </label>
            </div>
          </Card>

          {/* Default Targets -- only shown for custom SLA */}
          {slaSource === 'custom' && (
          <>
          {/* Default Targets */}
          <Card className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 dark:text-gray-100">Default SLA Targets</h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                  First Response Time
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={config.defaultTargets.firstResponseMinutes}
                    onChange={(e) =>
                      handleChange('defaultTargets.firstResponseMinutes', parseInt(e.target.value) || 60)
                    }
                    min={1}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                  />
                  <span className="text-sm text-gray-500 dark:text-gray-400">minutes</span>
                </div>
                <p className="text-xs text-gray-400 mt-1 dark:text-gray-500">
                  = {formatMinutes(config.defaultTargets.firstResponseMinutes)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                  Resolution Time
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={config.defaultTargets.resolutionMinutes}
                    onChange={(e) =>
                      handleChange('defaultTargets.resolutionMinutes', parseInt(e.target.value) || 480)
                    }
                    min={1}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                  />
                  <span className="text-sm text-gray-500 dark:text-gray-400">minutes</span>
                </div>
                <p className="text-xs text-gray-400 mt-1 dark:text-gray-500">
                  = {formatMinutes(config.defaultTargets.resolutionMinutes)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                  Warning Threshold
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={config.defaultTargets.warningThresholdPercent}
                    onChange={(e) =>
                      handleChange('defaultTargets.warningThresholdPercent', parseInt(e.target.value) || 80)
                    }
                    min={1}
                    max={99}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                  />
                  <span className="text-sm text-gray-500 dark:text-gray-400">%</span>
                </div>
                <p className="text-xs text-gray-400 mt-1 dark:text-gray-500">
                  Show warning when this % of time has passed
                </p>
              </div>
            </div>
          </Card>

          {/* Priority Overrides */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Priority-Based Overrides</h3>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-200">Priority</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-200">First Response</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-200">Resolution</th>
                  </tr>
                </thead>
                <tbody>
                  {PRIORITIES.map((priority) => {
                    const override = config.priorityOverrides?.[priority] || {};
                    const firstResponse = override.firstResponseMinutes ?? config.defaultTargets.firstResponseMinutes;
                    const resolution = override.resolutionMinutes ?? config.defaultTargets.resolutionMinutes;

                    return (
                      <tr key={priority} className="border-b border-gray-100 dark:border-gray-800">
                        <td className="py-2 px-3">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium capitalize
                              ${priority === 'urgent' ? 'bg-red-100 text-red-700' : ''}
                              ${priority === 'high' ? 'bg-orange-100 text-orange-700' : ''}
                              ${priority === 'medium' ? 'bg-blue-100 text-blue-700' : ''}
                              ${priority === 'low' ? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200' : ''}
                            `}
                          >
                            {priority}
                          </span>
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={firstResponse}
                              onChange={(e) =>
                                handlePriorityOverride(priority, 'firstResponseMinutes', parseInt(e.target.value) || 60)
                              }
                              min={1}
                              className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                            />
                            <span className="text-xs text-gray-400 dark:text-gray-500">{formatMinutes(firstResponse)}</span>
                          </div>
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={resolution}
                              onChange={(e) =>
                                handlePriorityOverride(priority, 'resolutionMinutes', parseInt(e.target.value) || 480)
                              }
                              min={1}
                              className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                            />
                            <span className="text-xs text-gray-400 dark:text-gray-500">{formatMinutes(resolution)}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Behavior Settings */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Pause className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">SLA Behavior</h3>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg dark:bg-gray-900">
                <div>
                  <p className="font-medium text-gray-700 dark:text-gray-200">Business Hours Only</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Only count SLA time during business hours
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.businessHoursOnly ?? true}
                    onChange={(e) => handleChange('businessHoursOnly', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 dark:bg-gray-700"></div>
                </label>
              </div>

              {(config.businessHoursOnly ?? true) && (
                <div className="p-4 bg-gray-50 rounded-lg dark:bg-gray-900">
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                    Business Hours Schedule
                  </label>
                  <ThemedSelect
                    value={config.businessHoursId ?? ''}
                    onChange={(e) => handleChange('businessHoursId', e.target.value || null)}
                    className="w-full max-w-xs px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Tenant default schedule</option>
                    {schedules.map((s) => (
                      <option key={s.id} value={s.id} disabled={!s.isActive}>
                        {s.name}
                        {s.isDefault ? ' — Default' : ''}
                        {!s.isActive ? ' (disabled)' : ''}
                      </option>
                    ))}
                  </ThemedSelect>
                  {schedules.length === 0 ? (
                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                      No business hours schedules found. Create one under Settings &gt; Business Hours.
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      SLA timers count only inside this schedule&rsquo;s hours. Leave as tenant default
                      to use the default schedule.
                    </p>
                  )}
                </div>
              )}
            </div>
          </Card>
          </>
          )}

          {/* Pause conditions -- pipeline-level, applies to both SLA sources */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Pause className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">SLA Pause</h3>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg dark:bg-gray-900">
              <div>
                <p className="font-medium text-gray-700 dark:text-gray-200">Pause SLA while Pending</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Stop the SLA clock while a ticket is Pending (waiting on the customer). When it leaves
                  Pending, its deadlines move out by the time it was paused.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  aria-label="Pause SLA while Pending"
                  checked={config.pauseOnPending ?? false}
                  onChange={(e) => handleChange('pauseOnPending', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 dark:bg-gray-700"></div>
              </label>
            </div>
          </Card>
        </>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 dark:bg-red-900/20 dark:border-red-800">
          <AlertTriangle className="h-5 w-5 text-red-500 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {hasChanges && (
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <Spinner size="sm" />
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save SLA Configuration
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}